// src/hooks/useStylistIdleLogout.ts
//
// Core hook for the stylist web inactivity-logout feature.
//
// Active ONLY when ALL of:
//   1. isAuthenticated === true
//   2. user.userType / user.role === 'stylist'
//   3. featureFlags.stylistIdleLogoutEnabled !== false  (default true when absent)
//
// Cross-tab mechanism (react-idle-timer crossTab + syncTimers):
//   - Activity in ANY open tab resets the shared idle countdown in ALL tabs.
//   - onPrompt fires in ALL tabs simultaneously when idle-minus-warning is reached.
//   - Calling activate() from "Stay signed in" broadcasts onActive to ALL tabs,
//     which causes every tab to close its own warning modal (local useState driven
//     by cross-tab events — no shared cross-tab modal state needed).
//   - onIdle fires in ALL tabs; only the tab that was last leader executes logout.
//     Using stopOnIdle: true prevents the timer from auto-resetting so logout
//     fires exactly once per session expiry.
//
// Sleep/wake robustness:
//   - react-idle-timer v5 stores last-active wall-clock time in localStorage/IDB.
//   - syncTimers: 200 means each tab re-checks elapsed time every 200 ms.
//   - On wake-up, the first sync interval fires and computes getRemainingTime() ≤ 0,
//     which immediately triggers onIdle even if the device slept through the deadline.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';
import { useAppDispatch, useAppSelector } from './useRedux';
import { logout } from '../store/slices/authSlice';
import { useUI } from '../context/UIContext';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { useIdleLogoutContext } from '../context/IdleLogoutContext';
import { IDLE_SYNC_INTERVAL_MS } from '../constants/idleTimeout';

export interface UseStylistIdleLogoutReturn {
  /** Whether the "Are you still there?" warning modal should be shown. */
  showWarning: boolean;
  /** Milliseconds remaining in the warning countdown (counts down from IDLE_PROMPT_BEFORE_MS). */
  remainingMs: number;
  /** Call when the user clicks "Stay signed in". Resets and broadcasts to all tabs. */
  onStaySignedIn: () => void;
  /** Whether the idle timer is currently active for this session. */
  isActive: boolean;
}

export const useStylistIdleLogout = (): UseStylistIdleLogoutReturn => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showSnackbar } = useUI();
  const { user, isAuthenticated } = useAppSelector((s) => s.auth);
  const { stylistIdleLogoutEnabled, idleTimeoutMinutes, warningMinutes } = useFeatureFlags();
  const { isPaused } = useIdleLogoutContext();

  const idleTimeoutMs = idleTimeoutMinutes * 60 * 1000;
  const promptBeforeMs = warningMinutes * 60 * 1000;

  const role = user?.userType || user?.role;
  // Timer is only active for authenticated stylists when the kill-switch is on (default true)
  const isActive =
    isAuthenticated === true &&
    role === 'stylist' &&
    stylistIdleLogoutEnabled !== false;

  const [showWarning, setShowWarning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(promptBeforeMs);

  // Interval ref for the in-modal countdown
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Guard against concurrent logout calls (e.g. fast sleep/wake + cross-tab)
  const isLoggingOutRef = useRef(false);

  const stopCountdown = useCallback(() => {
    if (countdownRef.current !== null) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // onPrompt fires in ALL tabs via crossTab when the warning window begins
  const handlePrompt = useCallback(() => {
    setShowWarning(true);
    setRemainingMs(promptBeforeMs);
    stopCountdown();
    countdownRef.current = setInterval(() => {
      setRemainingMs((prev) => Math.max(0, prev - 1_000));
    }, 1_000);
  }, [promptBeforeMs, stopCountdown]);

  // onIdle fires in ALL tabs via crossTab; guard with ref so logout runs once per tab.
  //
  // Multi-tab logout (Item 2):
  //   All open tabs dispatch logout() simultaneously. The first tab's POST /auth/logout
  //   succeeds; subsequent tabs get 401 (token already cleared). The thunk's rejected
  //   branch explicitly sets s.error = null (not the error message), so no error
  //   snackbar is shown. Every tab independently navigates to /login with the info snackbar.
  //
  // Wi-Fi down after wake (Item 4):
  //   navigate('/login') and showSnackbar() are OUTSIDE the try/catch and always execute
  //   even when the API call fails. The thunk also clears tokens on rejection, so the
  //   user is fully logged out locally regardless of network state.
  const handleIdle = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    stopCountdown();
    setShowWarning(false);
    try {
      await dispatch(logout()).unwrap();
    } catch {
      // Tokens are cleared inside the thunk even when the API call fails.
      // logout.rejected sets s.error = null so no error snackbar fires (authSlice L368).
    }
    // navigate + snackbar are intentionally outside try/catch and thunk suppresses
    // logout.rejected errors (authSlice L368) — redirect must always run even if
    // POST /auth/logout fails (e.g., Wi-Fi not yet up after wake).
    navigate('/login');
    showSnackbar('You were signed out due to inactivity.', 'info');
  }, [dispatch, navigate, showSnackbar, stopCountdown]);

  // onActive fires in ALL tabs when ANY tab calls activate() ("Stay signed in")
  const handleActive = useCallback(() => {
    stopCountdown();
    setShowWarning(false);
    isLoggingOutRef.current = false;
  }, [stopCountdown]);

  const idleTimer = useIdleTimer({
    timeout: idleTimeoutMs,
    promptBeforeIdle: promptBeforeMs,
    crossTab: true,
    syncTimers: IDLE_SYNC_INTERVAL_MS,
    onPrompt: handlePrompt,
    onIdle: handleIdle,
    onActive: handleActive,
    // Completely disabled (no listeners attached) when role is not stylist
    disabled: !isActive,
    // After onIdle, timer stays idle until activate()/reset() is called;
    // this prevents a second onIdle call if the user somehow generates an event
    stopOnIdle: true,
  });

  // Respond to pause/resume requests from IdleLogoutContext
  useEffect(() => {
    if (!isActive) return;
    if (isPaused) {
      idleTimer.pause();
    } else {
      idleTimer.resume();
    }
  }, [isPaused, isActive, idleTimer]);

  // If the timer becomes inactive mid-warning (kill-switch toggled, role changed),
  // close the modal and reset so there is no stuck "00:00" state with no dismiss path.
  useEffect(() => {
    if (!isActive) {
      setShowWarning(false);
      stopCountdown();
      setRemainingMs(promptBeforeMs);
      isLoggingOutRef.current = false;
    }
  }, [isActive, promptBeforeMs, stopCountdown]);

  // Cleanup the countdown interval on unmount
  useEffect(() => () => stopCountdown(), [stopCountdown]);

  const onStaySignedIn = useCallback(() => {
    // activate() resets the timer AND broadcasts onActive to all crossTab instances,
    // causing every open tab to close its warning modal via handleActive above.
    idleTimer.activate();
  }, [idleTimer]);

  return { showWarning, remainingMs, onStaySignedIn, isActive };
};
